/**
 * TypesPane — the workbench's types-view primitive.
 *
 * Binds the `TypesMode` view to the workspace services: the model comes from the
 * shared compile (WorkspaceModelProvider) and the selected type from the store's
 * `selectedTypeName` slice (the same slice BoardPane reads to highlight that
 * type's instances). The viewer mounts it in types mode in place of its old
 * prop-wired TypesMode.
 *
 * Routing handlers (`onJumpToInstance`, `onArchlangNav`) stay props — jumping to
 * a module / switching modes is shell orchestration.
 */
import type { ReactElement } from "react";
import type { StableId } from "@archlang/scene/arch-model";
import { TypesMode } from "../../components/TypesMode.js";
import { useWorkspace } from "../WorkspaceContext.js";
import { useSelectedTypeName } from "../WorkspaceStore.js";
import { useWorkspaceModel } from "../WorkspaceModelContext.js";

export interface TypesPaneProps {
  readonly onJumpToInstance?: (moduleId: StableId) => void;
  readonly onArchlangNav?: (href: string) => void;
}

export function TypesPane({ onJumpToInstance, onArchlangNav }: TypesPaneProps): ReactElement {
  const store = useWorkspace();
  const { model } = useWorkspaceModel();
  const selectedTypeName = useSelectedTypeName(store);
  return (
    <TypesMode
      model={model}
      selectedTypeName={selectedTypeName}
      onJumpToInstance={onJumpToInstance}
      onArchlangNav={onArchlangNav}
    />
  );
}
